// src/service/SessionInvalidationService.js
/**
 * Session Invalidation Service
 * Handles single active session enforcement by detecting concurrent logins
 * and invalidating sessions across all browser tabs/windows
 */

class SessionInvalidationService {
  static CHANNEL_NAME = 'rms-session-invalidation';
  static SESSION_ID_KEY = 'rms_session_id';
  static broadcastChannel = null;

  /**
   * Initialize the broadcast channel for cross-tab communication
   */
  static init() {
    if (typeof window === 'undefined') return;
    
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);
      
      // Listen for session invalidation messages from other tabs
      this.broadcastChannel.onmessage = (event) => {
        if (event.data) {
          if (event.data.type === 'SESSION_INVALIDATED') {
            const { sessionId, reason } = event.data;
            const currentSessionId = this.getCurrentSessionId();
            
            // If this session is being invalidated, clear it
            if (currentSessionId && currentSessionId === sessionId) {
              this.handleSessionInvalidation(reason || 'Another session was started on a different device or browser');
            }
          } else if (event.data.type === 'NEW_SESSION') {
            // New session detected - invalidate current session if it's different
            const newSessionId = event.data.sessionId;
            const currentSessionId = this.getCurrentSessionId();
            if (currentSessionId && currentSessionId !== newSessionId) {
              this.handleSessionInvalidation('A new session was started on another device or browser');
            }
          }
        }
      };
    }

    // Fallback: Use localStorage events for cross-tab communication
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'rms_session_invalidation') {
          const data = JSON.parse(event.newValue || '{}');
          if (data.type === 'SESSION_INVALIDATED') {
            const currentSessionId = this.getCurrentSessionId();
            if (currentSessionId && currentSessionId === data.sessionId) {
              this.handleSessionInvalidation(data.reason || 'Another session was started');
            }
          }
        }
      });
    }
  }

  /**
   * Generate a unique session ID
   */
  static generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session ID
   */
  static getCurrentSessionId() {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(this.SESSION_ID_KEY);
  }

  /**
   * Set current session ID
   */
  static setCurrentSessionId(sessionId) {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(this.SESSION_ID_KEY, sessionId);
  }

  /**
   * Clear current session ID
   */
  static clearCurrentSessionId() {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(this.SESSION_ID_KEY);
  }

  /**
   * Broadcast session invalidation to all tabs
   */
  static broadcastSessionInvalidation(sessionId, reason) {
    const message = {
      type: 'SESSION_INVALIDATED',
      sessionId,
      reason,
      timestamp: Date.now()
    };

    // Use BroadcastChannel if available
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message);
    }

    // Fallback: Use localStorage event
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('rms_session_invalidation', JSON.stringify(message));
        // Remove immediately to trigger storage event
        setTimeout(() => {
          localStorage.removeItem('rms_session_invalidation');
        }, 100);
      } catch (e) {
        console.warn('Failed to broadcast session invalidation via localStorage:', e);
      }
    }
  }

  /**
   * Broadcast new session creation
   */
  static broadcastNewSession(sessionId) {
    const message = {
      type: 'NEW_SESSION',
      sessionId,
      timestamp: Date.now()
    };

    // Use BroadcastChannel if available
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message);
    }

    // Fallback: Use localStorage event
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('rms_new_session', JSON.stringify(message));
        // Trigger storage event by removing and re-adding
        setTimeout(() => {
          localStorage.removeItem('rms_new_session');
        }, 100);
      } catch (e) {
        console.warn('Failed to broadcast new session via localStorage:', e);
      }
    }
  }

  /**
   * Handle session invalidation
   */
  static handleSessionInvalidation(reason) {
    // Clear session data
    this.clearCurrentSessionId();
    
    // Import SessionManager dynamically to avoid circular dependencies
    import('./SessionManager').then(({ default: SessionManager }) => {
      SessionManager.clearSession();
      
      // Show user-friendly message
      const message = reason || 'Your session has been invalidated because you logged in on another device or browser.';
      
      // Store message for display on login page
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('rms_session_invalidation_reason', message);
      }
      
      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    });
  }

  /**
   * Check if session was invalidated and get reason
   */
  static getInvalidationReason() {
    if (typeof window === 'undefined') return null;
    const reason = sessionStorage.getItem('rms_session_invalidation_reason');
    if (reason) {
      sessionStorage.removeItem('rms_session_invalidation_reason');
      return reason;
    }
    return null;
  }

  /**
   * Cleanup: Close broadcast channel
   */
  static cleanup() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }
}

// Auto-initialize on module load
if (typeof window !== 'undefined') {
  SessionInvalidationService.init();
}

export default SessionInvalidationService;

